import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, Output, inject } from '@angular/core';

import { OfferCategory } from '@cds/betting-offer';
import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterButtonSize } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { GRID_CONFIG, GridConfig } from '@frontend/sports/grid/core/feature/config';
import { CollapsedState, GridEvent, GridMedia, Group } from '@frontend/sports/grid/core/feature/model';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { OptionContextService } from '@frontend/sports/price-boost/feature';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { ContentReadyTriggerDirective, NativeAppService } from '@frontend/vanilla/core';

import { ErrorComponent } from '../../common/error/error.component';
import { CompactEventService } from '../../grid-base/compact-event.service';
import { OptionPanelComponent } from '../../option-group/option-panel.component';
import { EventHeaderScoreboardComponent } from '../header/event-header-scoreboard.component';
import { EventHeaderComponent } from '../header/event-header.component';

@Component({
    selector: 'ms-compact-event',
    templateUrl: './compact-event.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EventHeaderComponent, OptionPanelComponent, ErrorComponent, EventHeaderScoreboardComponent],
    hostDirectives: [ContentReadyTriggerDirective],
})
export class CompactEventComponent implements OnChanges {
    sitecore = inject(Sitecore);
    config = inject(MarketGroupingConfig);
    private urlHelper = inject(UrlHelperService);
    private compactEventService = inject(CompactEventService);
    private nativeAppService = inject(NativeAppService);
    private gridConfig = inject<GridConfig | null>(GRID_CONFIG, { optional: true });
    private optionContextService = inject(OptionContextService, { optional: true });

    @Input() event: GridEvent;
    @Input() isOutrightMarket: boolean;
    @Input() groups?: Group[];
    @Input() tracking: TrackingSource;
    @Input() media: GridMedia;
    @Input() state?: CollapsedState;
    @Input() showAllBetsButton?: boolean;
    @Output() collapse = new EventEmitter<string | undefined>();
    @Output() eventClick = new EventEmitter<Event>();
    @HostBinding('class') className = 'grid-compact-event';
    @Input() betDetails = '';
    @Input() hideOptionPanelHeaderForPriceBoost: boolean;
    @Input() sourceOfPick: SourceOfPick;
    @Input() showParticipantImage: boolean;
    @Input() displayCompactScoreboardEvent: boolean;

    optionGroups: EventOptionGroup[] = [];
    eventUrl: string;
    isMarketGroupToggable: boolean;
    isAllBetsButtonVisible: boolean;
    isEventNameALink: boolean;
    scrollAdapterButtonSize: ScrollAdapterButtonSize;
    isOptionGroupLinkable: boolean;

    constructor() {
        this.isOptionGroupLinkable = this.gridConfig?.isOptionGroupLinkable ?? false;
        this.isMarketGroupToggable = this.gridConfig?.isMarketGroupToggable ?? true;
        this.isAllBetsButtonVisible = this.gridConfig?.isAllBetsButtonVisible ?? true;
        this.isEventNameALink = this.gridConfig?.isEventNameALink ?? false;
        this.scrollAdapterButtonSize = this.gridConfig?.isScrollAdapterButtonSizeLarge
            ? ScrollAdapterButtonSize.Large
            : ScrollAdapterButtonSize.Small;
    }

    ngOnChanges(changes: ISimpleChanges<CompactEventComponent>): void {
        const isNewEvent = changes.event && (!changes.event.previousValue || changes.event.previousValue.id !== changes.event.currentValue.id);
        if (isNewEvent) {
            this.eventUrl = this.urlHelper.getEventUrl(this.event);
        }

        if (changes.event || changes.groups) {
            this.setOptionGroups();
        }

        if (changes.showAllBetsButton) {
            this.isAllBetsButtonVisible = changes.showAllBetsButton?.currentValue ?? this.gridConfig?.isAllBetsButtonVisible ?? true;
        }
    }

    get trackingSource(): string {
        return this.tracking.source.slice(0, this.tracking.source.lastIndexOf('/'));
    }

    toggle(): void {
        this.collapse.emit();
    }

    collapsed(optionGroup: EventOptionGroup): boolean {
        return !!this.state && this.state.collapsedChildren.indexOf(optionGroup.id) !== -1;
    }

    collapsedChanged(optionGroup: EventOptionGroup): void {
        this.collapse.emit(optionGroup.id);
    }

    showCompactScoreboardEvent(): boolean {
        return this.displayCompactScoreboardEvent && !this.event.offerCategories.includes(OfferCategory.Outrights);
    }

    private setOptionGroups(): void {
        if (!this.event.optionGroups || !this.event.optionGroups.length) {
            this.optionGroups = [];

            return;
        }

        this.optionGroups = this.compactEventService.prepareEventOptionGroups(this.event.optionGroups, this.groups);
        if (!this.optionContextService?.isPriceBoost) {
            return;
        }

        if (!this.nativeAppService.isTerminal) {
            this.optionGroups.forEach((optiongroup) => {
                if (!optiongroup.name.endsWith(this.sitecore.event.PriceBoostMarket)) {
                    optiongroup.name += ` - ${this.sitecore.event.PriceBoostMarket}`;
                }
            });
        }
    }
}
